import React, { useEffect, useState } from 'react';

import {
  CmBox,
  CmButton,
  CmCard,
  CmFlex,
  CmFormControl,
  CmFormLabel,
  CmHelpterText,
  CmLeftIconAndInput,
  CmList,
  CmListItem,
  CmScrollBox,
  CmText,
} from '@/components/uiParts';
import { SearchIcon } from '@/components/uiParts';
import { SearchItem } from '@/pages/menu-managements/services/searchItem';

type Props = {
  targetName:
    | '紐付けオプショングループ'
    | '商品'
    | 'オプション商品'
    | 'カテゴリ';
  brandSelectValue: string;
  isSearching: boolean;
  searchItemName: string;
  selectedItems: SearchItem[];
  searchItems: SearchItem[];
  handleSearchItemNameInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddSelectItems: (item: SearchItem) => void;
  handleClickMultipleSelect?: () => void;
};

const SEARCH_INPUT_WRAP = 'search-input-wrap' as const;

export const MenuManagementSearchAndDragArea: React.FC<Props> = ({
  brandSelectValue,
  targetName,
  isSearching,
  searchItemName,
  selectedItems,
  searchItems,
  handleSearchItemNameInput,
  handleAddSelectItems,
  handleClickMultipleSelect,
  children,
}): JSX.Element => {
  const [isSearchResultShow, setIsSearchResultShow] = useState<boolean>(false);

  useEffect(() => {
    const searchInputWrap = document.getElementById(
      SEARCH_INPUT_WRAP
    ) as HTMLElement;

    const handleClickOutside = (event: MouseEvent) => {
      const eventTarget = event.target as Node;
      // 参考; https://yamatooo.blog/entry/2021/06/21/083000
      if (searchInputWrap && !searchInputWrap.contains(eventTarget)) {
        setIsSearchResultShow(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const filterdSearchItems = searchItems.filter((searchItem) => {
    return selectedItems.every((selectedItem) => {
      return selectedItem.id !== searchItem.id;
    });
  });

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearchItemNameInput(e);
    setIsSearchResultShow(true);
  };

  const isExistFilterdSearchItems = filterdSearchItems.length > 0;

  const notExistItemMessage = (() => {
    // 商品が存在しない。もしくは全て選択されている。
    if (
      (searchItems.length === 0 && searchItemName === '') ||
      searchItems.length === selectedItems.length
    )
      return `選択できる${targetName}がありませんでした。`;
    // 検索した文字列に一致しない。
    if (!isExistFilterdSearchItems && searchItemName !== '')
      return `検索内容に一致する${targetName}がありませんでした。`;
  })();

  return (
    <CmBox>
      <CmBox>
        <CmFlex justifyContent="space-between" alignItems="center">
          <CmBox
            color={brandSelectValue === '' ? '#696969!important' : ''}
            width={'full'}
          >
            <CmFormControl width="40%">
              <CmFormLabel htmlFor={SEARCH_INPUT_WRAP} fontWeight="bold">
                {`${targetName}選択`}
              </CmFormLabel>
              <CmBox display="flex">
                <CmLeftIconAndInput
                  id={SEARCH_INPUT_WRAP}
                  icon="search"
                  isDisabled={brandSelectValue === ''}
                  value={searchItemName}
                  onFocus={onSearch}
                  onChange={onSearch}
                />
                {handleClickMultipleSelect && (
                  <CmButton
                    padding={2}
                    marginLeft={4}
                    variant="link"
                    color="#3182ce"
                    fontWeight="normal"
                    onClick={handleClickMultipleSelect}
                  >
                    複数選択
                  </CmButton>
                )}
              </CmBox>
            </CmFormControl>
          </CmBox>
        </CmFlex>
        {!isSearching && isSearchResultShow && (
          <CmBox position="relative">
            <CmCard
              position="absolute"
              zIndex="1"
              width="70%"
              rounded="none"
              borderColor={'rgb(226, 232, 240)'}
              padding="0"
            >
              <CmScrollBox maxHeight="300px" pb="0">
                <CmList padding="0">
                  {isExistFilterdSearchItems ? (
                    filterdSearchItems.map((item, index) => (
                      <CmListItem
                        px="3"
                        py="3"
                        key={index}
                        display="flex"
                        alignItems="center"
                        tabIndex={0}
                        cursor="pointer"
                        onClick={() => handleAddSelectItems(item)}
                        _hover={{ backgroundColor: '#EDF2F7' }}
                      >
                        <SearchIcon color="gray.300" />
                        <CmBox ml="2">
                          <CmText>{item.title.translation.ja}</CmText>
                          {item.memo !== null && (
                            <CmHelpterText>{item.memo}</CmHelpterText>
                          )}
                        </CmBox>
                      </CmListItem>
                    ))
                  ) : (
                    <CmListItem
                      px="3"
                      py="3"
                      display="flex"
                      alignItems="center"
                    >
                      <SearchIcon color="gray.300" />
                      <CmText ml="2">{notExistItemMessage}</CmText>
                    </CmListItem>
                  )}
                </CmList>
              </CmScrollBox>
            </CmCard>
          </CmBox>
        )}
      </CmBox>
      {selectedItems.length > 0 && (
        <CmBox mt="10px">
          <CmScrollBox>{children}</CmScrollBox>
        </CmBox>
      )}
    </CmBox>
  );
};
